import { useCallback, useEffect, useRef, useState } from 'react';
import type { Channel as StreamChannel, StreamChat } from 'stream-chat';
import {
  UNREAD_CHATS_QUERY,
  type InboxFacet,
  type InboxFacetDim,
  type InboxQueryBody,
  type MessageHitDto,
} from '@chat-emp/shared';

import type { ApplicationStatus, InboxItem } from '../fixtures/domain.js';
import { APPLICATION_STATUSES, needsReply } from '../fixtures/rules.js';
import { compileInboxQuery, GROUP_STRATEGIES, rowFate, type InboxQuery } from '../inbox/query.js';
import { ApiRequestError } from '../lib/api.js';
import { cidParts } from '../lib/bridge.js';
import { facetInbox, fetchMessageHits, queryInbox } from '../lib/facade.js';
import { absorbUnlocks, hydrateChannels, mapChannel } from './stream.js';

/** Rows per page. Also the Stream hydration size, so the watch set is bounded. */
export const PAGE_SIZE = 25;

// The read model lags the SQS pipeline by seconds, so re-querying on the raw
// event returns a row-set without the new channel.
export const REALTIME_DEBOUNCE_MS = 1500;

// How often the aggregates — facets, totals, rail dots — are willing to
// re-read. A count ten seconds old is invisible; a missing list row is not,
// so the list sync stays on REALTIME_DEBOUNCE_MS. A cap rather than a
// debounce: a busy company's events must not keep postponing the re-read
// forever (AI-2956).
export const COUNT_REFRESH_MS = 10_000;

// Bounded: the failure may be the read model being down, and an unbounded
// re-arm would poll it for the rest of the session.
const RESYNC_ATTEMPTS = 3;

// A failed count has nothing to re-issue it on a quiet inbox, so the pill would
// stay blank for the session. Bounded for the same reason as above.
export const COUNT_RETRY_DELAYS_MS = [500, 1500] as const;

/** User-scoped events meaning "something you are not watching may now match". */
const ARRIVAL_EVENTS = [
  'notification.message_new',
  'notification.added_to_channel',
  'notification.removed_from_channel',
] as const;

// What moves a facet count: a conversation arriving, leaving or changing
// group. Reads stay out — a read moves only the unread cut, which rides
// SCOPE_STALE_EVENTS, and a panel faceting *over* that cut passes the scope
// set instead (AI-2956). `message.new` stays out too: a message on an
// already-counted conversation changes no count.
export const FACET_STALE_EVENTS = [
  ...ARRIVAL_EVENTS,
  'channel.updated',
  'channel.deleted',
] as const;

// What moves a cut: the facet events, plus reads — the only thing that
// shrinks the Unread pill without a conversation arriving or leaving — plus
// `message.new`, which makes a read chat unread again. Spelled out rather
// than spread from FACET_STALE_EVENTS, so taking an event out of one set
// cannot silently take it out of the other.
export const SCOPE_STALE_EVENTS = [
  ...ARRIVAL_EVENTS,
  'channel.updated',
  'channel.deleted',
  'notification.mark_read',
  'message.read',
  'message.new',
] as const;

/** Events on channels we already watch — handled locally, no round trip. */
const WATCHED_EVENTS = [
  'message.new',
  'message.updated',
  'message.deleted',
  'channel.updated',
  'channel.deleted',
  'notification.mark_read',
  // Stream sends this to a channel's watchers when someone reads it, which is
  // what arrives when the employer opens a conversation in this tab.
  'message.read',
] as const;

export interface UseInboxList {
  items: InboxItem[];
  /** Full filtered count from the read model, not the number loaded. */
  total: number;
  // Rows still shown that have left the cut (see `rowFate`). Only those the
  // latest answer still counts, or a read gets subtracted twice.
  settled: number;
  loading: boolean;
  /** The first page failed. Distinct from "no matches" — the UI must not
   *  render an empty list for a server that did not answer. */
  error: boolean;
  hasMore: boolean;
  /** Stream could not answer the body half, so these rows are name matches
   *  only. The panel says so rather than passing a partial answer off as whole. */
  searchDegraded: boolean;
  /** The walk stopped because the search behind it expired twice. The rows on
   *  screen are real; there are simply more the list can no longer reach. */
  searchExpired: boolean;
  /** The counterparts the search matched through their profile — the
   *  Candidates section's membership. Empty outside a search. */
  profileMatched: ReadonlySet<string>;
  loadingMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

const EMPTY: UseInboxList = {
  items: [],
  total: 0,
  settled: 0,
  loading: true,
  error: false,
  hasMore: false,
  searchDegraded: false,
  searchExpired: false,
  profileMatched: new Set<string>(),
  loadingMore: false,
  loadMore: () => {},
  reload: () => {},
};

interface Page {
  items: InboxItem[];
  channels: StreamChannel[];
  cursor: string;
  total: number;
  // Both, because a page is the authority on its own rows only: a later page must
  // not retire an earlier page's mark.
  pageIds: string[];
  flagged: Set<string>;
  searchDegraded: boolean;
  profileMatched: string[];
}

async function fetchPage(query: InboxQuery, client: StreamChat, cursor: string): Promise<Page> {
  const body = compileInboxQuery({ ...query, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) });
  const {
    channel_ids,
    next_cursor,
    total,
    assistant_flagged,
    application_statuses,
    profile_unlocked_channels,
    message_search_unavailable,
    profile_matched_counterparts,
  } = await queryInbox(body);
  absorbStageHints(application_statuses);
  absorbUnlocks(channel_ids, profile_unlocked_channels);
  const flagged = new Set(assistant_flagged ?? []);
  const mapped = await hydrateChannels(client, channel_ids, flagged);

  return {
    items: mapped.map((m) => ({
      channel: m.channel,
      counterpart: m.counterpart,
      needsReply: needsReply(m.channel),
    })),
    channels: mapped.map((m) => m.raw),
    cursor: next_cursor,
    total,
    pageIds: channel_ids,
    flagged,
    searchDegraded: message_search_unavailable === true,
    profileMatched: profile_matched_counterparts ?? [],
  };
}

/**
 * The stage each job chat had when its inbox page was read, so an opened thread
 * can draw its stage controls at once instead of waiting on `/details`. Module
 * level, like `detailsRevisions`: the thread may be hydrated by a path that
 * never saw the page. A hint for drawing only: it can be stale, or the read
 * model's seeded `pending`, so no stage move is pressable until `/details` lands.
 */
const stageHints = new Map<string, ApplicationStatus>();

export function absorbStageHints(
  statuses: { channel_id: string; status: string }[] | undefined,
): void {
  for (const { channel_id, status } of statuses ?? []) {
    if ((APPLICATION_STATUSES as string[]).includes(status)) {
      stageHints.set(cidParts(channel_id).channelId, status as ApplicationStatus);
    }
  }
}

export function stageHintFor(channelId: string): ApplicationStatus | undefined {
  return stageHints.get(cidParts(channelId).channelId);
}

// Scoped to the ids the page answered for. A blanket replace would unflag half
// the inbox on every "load more"; a blanket union would never let one go.
export function absorbFlags(into: Set<string>, page: Page): void {
  for (const id of page.pageIds) {
    if (page.flagged.has(id)) into.add(id);
    else into.delete(id);
  }
}

/** An answer's rows as `counted` holds them: id → the key it was ordered by. */
function namedBy(page: Page): [string, number][] {
  return page.items.map((i) => [i.channel.id, Date.parse(i.channel.lastActivityAt)]);
}

// `-Infinity` when an answer has no next cursor: it saw the whole set, so it
// retires everything. `Infinity` for an empty answer that still has a cursor.
function floorOf(page: Page): number {
  if (page.cursor === '') return -Infinity;
  return Math.min(...namedBy(page).map(([, at]) => at));
}

/**
 * One inbox list from an `InboxQuery`: read-model ids, hydrated and watched a
 * page at a time. Nothing unwatches: watching is state on the single WebSocket
 * `connectUser` opened, not a connection. Per-channel `stopWatching` costs an HTTP
 * request each and releasing a page at once tripped Stream's rate limit
 * (AI-2550); `disconnectUser` ends every watch in one step when chat closes.
 */
export function useInboxList(query: InboxQuery, client: StreamChat | null): UseInboxList {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [total, setTotal] = useState(0);
  /** Ids held past the cut — see `rowFate`. */
  const [heldIds, setHeldIds] = useState<string[]>([]);
  // id -> activity time. A held row only subtracts while the server still counts
  // it, and an answer speaks only for the stretch of the order it saw.
  const [counted, setCounted] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [cursor, setCursor] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [searchDegraded, setSearchDegraded] = useState(false);
  const [searchExpired, setSearchExpired] = useState(false);
  const [profileMatched, setProfileMatched] = useState<ReadonlySet<string>>(new Set());
  const [reloadKey, setReloadKey] = useState(0);

  /** Everything this hook is watching, so it can hand it all back. */
  const watched = useRef<StreamChannel[]>([]);
  /** Ids already on screen — the guard against a re-query duplicating rows. */
  const seen = useRef<Set<string>>(new Set());
  // The live re-derive below re-maps rows from Stream channels, which know
  // nothing about the assistant, so one message would clear every mark.
  const flagged = useRef<Set<string>>(new Set());

  // Serializing the query is what makes "the same filters" a stable dependency;
  // the object identity changes on every render of the panel holding it.
  const key = JSON.stringify(compileInboxQuery(query));

  // The realtime effects read the query through a ref rather than depending on
  // it. Depending on the object would re-run them on every render, and their
  // cleanup clears the debounce timer — so a re-render during the window would
  // silently cancel a pending sync and the arriving row would never appear.
  const queryRef = useRef(query);
  queryRef.current = query;

  // Bumped wherever the list starts over (a new cut, a `reload()`; `InboxPanel`
  // reloads after a bulk End). A compiled query cannot do this: it is
  // equal across a reload and returns to its old value when a filter is toggled
  // away and back, so a page in flight would pass and append dead rows.
  const generation = useRef(0);

  // Narrower than `generation`: an end-of-pages answer is not "the list started
  // over", and bumping the shared counter would kill sibling syncs too.
  const walk = useRef(0);

  // Whether this walk has already spent its one restart, so the second expiry
  // can stop rather than bounce. Cleared by the base load below on a real
  // change of question — never by the restart's own reload, which is the whole
  // point of the cap.
  const restarted = useRef(false);
  /** What `key` was when the budget above was last handed out. */
  const budgetKey = useRef(key);

  // Marked during render, not from an effect: an effect runs after paint, but a
  // promise settles on the microtask queue. Not commit-guaranteed.
  const keyRef = useRef(key);
  keyRef.current = key;

  // Orders answers across both fetchers. Compare `applied`, not `issued`: a
  // fetch that merely started must not invalidate an older one and then fail.
  const issued = useRef(0);
  const applied = useRef(0);

  /**
   * Ask the arrivals effect for a fresh page one. Set by that effect, so the
   * base load can hand a race back to the fetcher that is built to insert.
   */
  const resync = useRef<() => void>(() => {});

  useEffect(() => {
    if (!client) return;
    let alive = true;
    generation.current += 1;
    const seq = ++issued.current;

    setLoading(true);
    setError(false);
    setSearchExpired(false);

    // A different question is a different walk over a set the server resolves
    // afresh, so it gets its own restart. Keyed rather than unconditional
    // because this effect also runs on the restart's own `reloadKey` bump,
    // where clearing the budget would hand it straight back.
    if (budgetKey.current !== key) {
      budgetKey.current = key;
      restarted.current = false;
    }

    void fetchPage(query, client, '')
      .then((page) => {
        if (!alive) return;

        // A reset is not a straggler to be dropped: this IS the list now, and
        // returning here would leave a `reload()` — the one after a bulk End —
        // never applied. So it lands either way, and where a newer answer beat
        // it here, one sync is asked for afterwards to bring back whatever that
        // answer knew and this page was fetched too early to see.
        const overtaken = applied.current > seq;
        applied.current = Math.max(applied.current, seq);

        watched.current = page.channels;
        seen.current = new Set(page.items.map((i) => i.channel.id));
        // A first page is a fresh query, so its answer is the whole answer.
        flagged.current = new Set(page.flagged);
        setItems(page.items);
        setTotal(page.total);
        setSearchDegraded(page.searchDegraded);
        setProfileMatched(new Set(page.profileMatched));
        // The cut was just asked again, so nothing is being held past it: this
        // page IS the answer. Re-entering the Unread pill is what clears the
        // rows it held while the employer worked through them.
        setHeldIds([]);
        setCounted(new Map(namedBy(page)));
        setCursor(page.cursor);
        if (overtaken) resync.current();
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
    // `key` stands in for `query` deliberately — see above.
  }, [key, client, reloadKey]);

  // Re-derives every row from its (mutated in place) Stream channel. Removal
  // only: nothing here can introduce a row (see `rowFate`).
  useEffect(() => {
    if (!client) return;

    const reevaluate = () => {
      const uid = client.userID ?? '';
      const kept: StreamChannel[] = [];
      const dropped: StreamChannel[] = [];
      const rows: InboxItem[] = [];
      const heldNow: string[] = [];

      for (const raw of watched.current) {
        // The page's verdict, carried forward: Stream has no opinion on this
        // and re-mapping without it would retire the mark on every message.
        const m = mapChannel(raw, uid, flagged.current.has(raw.id ?? ''));
        const item: InboxItem = {
          channel: m.channel,
          counterpart: m.counterpart,
          needsReply: needsReply(m.channel),
        };
        const fate = rowFate(
          {
            phase: m.channel.phase,
            needsReply: item.needsReply,
            // The read model's own clause, mirrored locally (see `mapChannel`),
            // so opening the chat in another tab drops its row from the Unread
            // cut here — and drops it on the same terms the server would.
            unread: m.channel.unreadCount > 0,
            ...(m.channel.jobMeta ? { jobId: m.channel.jobMeta.jobId } : {}),
          },
          queryRef.current,
        );
        if (fate === 'gone') {
          dropped.push(raw);
          seen.current.delete(m.channel.id);
          continue;
        }
        // Held rows stay watched and stay `seen`, so they are still re-derived
        // on the next event — a candidate writing again puts one straight back
        // in the cut — and a re-query cannot insert a second copy of them.
        if (fate === 'settled') heldNow.push(m.channel.id);
        kept.push(raw);
        rows.push(item);
      }

      if (dropped.length > 0) watched.current = kept;

      // Sort locally to the same key the read model orders by, so a new message
      // moves its row without waiting for the re-query.
      rows.sort(
        (a, b) => Date.parse(b.channel.lastActivityAt) - Date.parse(a.channel.lastActivityAt),
      );
      setItems(rows);
      setHeldIds(heldNow);
    };

    const subs = WATCHED_EVENTS.map((e) => client.on(e, reevaluate));
    // The online dot. The SDK has already patched the member's `online` by the
    // time this runs (presence is on in hydrateChannels), so a re-map is all it
    // takes. Only for someone this list shows: Stream sends it for every member
    // of every watched channel, the employer's own other tabs included, and each
    // re-map re-renders the whole list.
    subs.push(
      client.on('user.presence.changed', (event) => {
        const id = event.user?.id;
        if (!id || id === client.userID) return;
        if (watched.current.some((raw) => raw.state.members[id])) reevaluate();
      }),
    );
    return () => subs.forEach((sub) => sub.unsubscribe());
  }, [client, key]);

  // Hydrating by a fixed id set means Stream will not insert a newly-matching
  // channel, so a notification triggers a debounced re-query of the first page.
  useEffect(() => {
    if (!client) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;

    // A sync fired by a notification needs no retry, another is coming. One fired
    // by `resync` has no successor, so the debt outlives the attempt.
    let owed = 0;

    // Two syncs are in flight together whenever a request outlasts the debounce.
    // Landing the older one last writes its pre-arrival `total` and retires the
    // just-arrived row from `counted`, so the pill under-counts.
    const sync = () => {
      const seq = ++issued.current;
      const askedKey = keyRef.current;
      const askedGen = generation.current;
      void fetchPage(queryRef.current, client, '')
        .then((page) => {
          // `alive` only fires after paint; `keyRef` marks during render, closing
          // the microtask gap. `generation` covers a reload, which tears nothing
          // down here. Not `walk`: a sibling sync is not stale for being overtaken.
          if (!alive || keyRef.current !== askedKey || generation.current !== askedGen) return;
          if (seq < applied.current) return;
          applied.current = seq;
          // An answer landed, so whatever the base load handed back is
          // recovered, by this sync or by one that beat it here.
          owed = 0;
          const fresh = page.items.filter((i) => !seen.current.has(i.channel.id));
          setTotal(page.total);
          setSearchDegraded(page.searchDegraded);
          setProfileMatched(new Set(page.profileMatched));
          // Ahead of any early return below: a sync that brings no new rows can
          // still change what the assistant makes of the ones already here.
          absorbFlags(flagged.current, page);

          // An answer speaks from the top of the order down to its oldest row, so
          // beliefs below it survive and everything else it did not name retires.
          // Strictly past: a row sharing the oldest timestamp may be the next page.
          const floor = floorOf(page);
          setCounted((prev) => {
            const next = new Map<string, number>();
            for (const [id, at] of prev) if (at <= floor) next.set(id, at);
            for (const [id, at] of namedBy(page)) next.set(id, at);
            return next;
          });
          // No next cursor, so the walk is over. Leaving `cursor` would keep
          // `hasMore` true and let the sentinel re-seed stale rows. `walk`, not
          // `generation`: a sibling sync with a newer answer must survive.
          if (page.cursor === '') {
            walk.current += 1;
            setCursor('');
          }
          if (fresh.length === 0) return;

          // Only the genuinely new channels join the row set; the rest of the
          // page is already held by this hook.
          const freshIds = new Set(fresh.map((i) => i.channel.id));
          const kept = page.channels.filter((c) => freshIds.has(c.cid));

          for (const id of freshIds) seen.current.add(id);
          watched.current = [...watched.current, ...kept];
          setItems((current) =>
            [...fresh, ...current].sort(
              (a, b) => Date.parse(b.channel.lastActivityAt) - Date.parse(a.channel.lastActivityAt),
            ),
          );
        })
        .catch(() => {
          // The next notification retries, unless a resync is owed: then nothing
          // is coming, and giving up drops the arrival silently.
          if (alive && owed > 0) {
            owed -= 1;
            schedule();
          }
        });
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(sync, REALTIME_DEBOUNCE_MS);
    };

    // Under a cut a message is an arrival: only the read model can put a row
    // back, local rules may only remove. Not an arrival for an unscoped list.
    // This hook watches everything it lists, so `message.new` is all that arrives.
    const cut = queryRef.current.filters.some((f) => f.kind === 'scope');
    const events = cut ? [...ARRIVAL_EVENTS, 'message.new'] : ARRIVAL_EVENTS;

    // What the base load calls when a newer answer beat its reset home. It takes
    // on the retry debt as well as arming the timer: this is the one caller with
    // no notification behind it to ask again.
    resync.current = () => {
      owed = RESYNC_ATTEMPTS;
      schedule();
    };

    const subs = events.map((e) => client.on(e, schedule));
    return () => {
      alive = false;
      clearTimeout(timer);
      resync.current = () => {};
      subs.forEach((sub) => sub.unsubscribe());
    };
    // `key` covers `cut` too: the scope clause is part of the compiled query.
  }, [client, key]);

  const loadMore = useCallback(() => {
    if (!client || !cursor || loadingMore) return;
    setLoadingMore(true);

    const askedKey = key;
    const askedGen = generation.current;
    const askedWalk = walk.current;

    void fetchPage(query, client, cursor)
      .then((page) => {
        // A page that lands after the list started over belongs to a question
        // nobody is asking any more. It used to write its rows and its total
        // anyway, and the next `sync` scrubbed that off by replacing the counted
        // set whole; now that a belief can survive a sync, the pollution would
        // outlive the list that produced it — a subtraction against a total it
        // never described, and a cursor into a walk that is over.
        if (
          keyRef.current !== askedKey ||
          generation.current !== askedGen ||
          walk.current !== askedWalk
        ) {
          return;
        }

        // Dedup against what is already on screen: the read model is
        // eventually consistent, so a row can shift pages between calls.
        const fresh = page.items.filter((i) => !seen.current.has(i.channel.id));
        for (const i of fresh) seen.current.add(i.channel.id);
        absorbFlags(flagged.current, page);
        // Only genuinely new channels join the watch set. The read model is
        // eventually consistent and can return a channel on two pages; a double
        // entry meant a duplicate row and an id subtracted twice from the pill.
        const freshIds = new Set(fresh.map((i) => i.channel.id));
        watched.current = [...watched.current, ...page.channels.filter((c) => freshIds.has(c.cid))];
        setItems((current) => [...current, ...fresh]);
        setTotal(page.total);
        // A page further down the same order: it names rows the server counts,
        // and it saw nothing above itself, so it only adds. Whole `page.items`
        // rather than `fresh` — a row the read model returned on two pages is
        // still a row it counts, and recording it twice is one map entry either
        // way, so the dedup that the row list and the watch set need does not
        // apply to what is merely a belief keyed by id.
        setCounted((prev) => new Map([...prev, ...namedBy(page)]));
        setCursor(page.cursor);
      })
      .catch((cause: unknown) => {
        // The same guard the success path carries, and for a sharper reason: a
        // 409 belonging to a walk nobody is on would otherwise reload whatever
        // list replaced it, throwing away a page one it has no quarrel with.
        if (
          keyRef.current !== askedKey ||
          generation.current !== askedGen ||
          walk.current !== askedWalk
        ) {
          return;
        }

        // A body search's channel set is held server-side for the walk, and a
        // cursor page is refused rather than re-resolved once it has gone (a
        // set that moves mid-walk skips and duplicates rows). The cursor can
        // then only fail again, so the walk starts over — swallowing it left
        // the sentinel re-firing a dead cursor. `reload` re-resolves the
        // search, which is what expired.
        if (cause instanceof ApiRequestError && cause.code === 'search_expired') {
          // Once per search. A set that expires again as fast as it is remade
          // (Stream search down: `FAIL_MS` is five seconds) would bounce the
          // employer to the top of the list for as long as they kept scrolling,
          // re-reading the whole first page each time. Twice is the answer that
          // restarting cannot fix, so the walk stops where it is: the rows
          // already loaded are real, and only paging has nowhere to go.
          if (restarted.current) {
            walk.current += 1;
            setCursor('');
            setSearchExpired(true);
            return;
          }
          restarted.current = true;
          setReloadKey((n) => n + 1);
          return;
        }
        // Any other failed page is not a failed list — the rows already
        // rendered stay, and the user can scroll again to retry.
      })
      .finally(() => setLoadingMore(false));
    // `key` is derived from `query`, already a dep; named so the guard reads.
  }, [client, cursor, loadingMore, query, key]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  // Derived rather than stored, so the two facts behind it can never be stale
  // relative to each other: a held row counts only while the read model is
  // believed to still count it. See `counted` for how a belief is earned, kept
  // and retired.
  const settled = heldIds.reduce((n, id) => (counted.has(id) ? n + 1 : n), 0);

  if (!client) return EMPTY;

  return {
    items,
    total,
    settled,
    loading,
    error,
    hasMore: cursor !== '',
    searchDegraded,
    searchExpired,
    profileMatched,
    loadingMore,
    loadMore,
    reload,
  };
}

/**
 * At most one fire per `window`, each at least REALTIME_DEBOUNCE_MS after the
 * event that asked for it (the read model lags the pipeline; see above). Not
 * a debounce: an event arriving while a fire is pending rides that fire
 * instead of postponing it, so a busy company cannot starve the re-read.
 */
function throttled(fire: () => void, window: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let last = 0;
  return {
    ask() {
      if (timer !== undefined) return;
      const wait = Math.max(REALTIME_DEBOUNCE_MS, last + window - Date.now());
      timer = setTimeout(() => {
        timer = undefined;
        last = Date.now();
        fire();
      }, wait);
    },
    cancel() {
      clearTimeout(timer);
      timer = undefined;
    },
  };
}

// Advances when the matching set may have changed. Throttled to
// COUNT_REFRESH_MS: everything riding it is a count, and a count may lag.
export function useInboxRevision(
  client: StreamChat | null,
  events: readonly string[] = FACET_STALE_EVENTS,
): number {
  const [revision, setRevision] = useState(0);
  const key = events.join();

  useEffect(() => {
    if (!client) return;
    const gate = throttled(() => setRevision((n) => n + 1), COUNT_REFRESH_MS);
    const subs = events.map((e) => client.on(e, gate.ask));
    return () => {
      gate.cancel();
      subs.forEach((sub) => sub.unsubscribe());
    };
    // Keyed on the joined names, not `events` itself: a caller passing a fresh
    // array each render would otherwise resubscribe on every one.
  }, [client, key]);

  return revision;
}

export interface UseInboxFacets {
  facets: InboxFacet[];
  loading: boolean;
  /**
   * Conversations whose candidate says nothing on this dimension. Candidate
   * dimensions only — the column-backed ones never send it, and 0 and absent
   * are not the same answer, so it stays optional rather than defaulting.
   */
  unknownCount?: number;
  /** The candidate set was too large to resolve; counts are a floor. */
  truncated?: boolean;
}

/**
 * A count that survives its own re-count.
 *
 * `useInboxTotal` blanks when its query changes, which is right for a number on
 * screen and wrong for anything deciding whether a row exists. This holds the
 * last answer through the gap; `active` false clears it.
 */
export function useHeldTotal(current: number | null, active: boolean): number | null {
  const [held, setHeld] = useState<number | null>(null);

  useEffect(() => {
    if (!active) setHeld(null);
    else if (current !== null) setHeld(current);
  }, [active, current]);

  return held;
}

/**
 * How many conversations a query matches, without hydrating any of them.
 *
 * `limit: 1` because only `total` is read, and that is a scoped COUNT
 * independent of the limit. Null until the first answer lands: zero is a real
 * result here, so it cannot double as "not asked yet".
 */
export function useInboxTotal(query: InboxQuery, enabled: boolean, revision = 0): number | null {
  // Held with the query it answers, so a filter change blanks the number while a
  // `revision` refresh keeps the last answer on screen.
  const [answer, setAnswer] = useState<{ key: string; total: number } | null>(null);

  const key = JSON.stringify(compileInboxQuery(query));

  useEffect(() => {
    if (!enabled) {
      setAnswer(null);
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const ask = (attempt: number) => {
      void queryInbox({ ...compileInboxQuery(query), limit: 1 })
        .then((page) => {
          if (alive) setAnswer({ key, total: page.total });
        })
        .catch(() => {
          if (alive && attempt < COUNT_RETRY_DELAYS_MS.length) {
            timer = setTimeout(() => ask(attempt + 1), COUNT_RETRY_DELAYS_MS[attempt]);
          }
        });
    };
    ask(0);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [key, enabled, revision]);

  return answer?.key === key ? answer.total : null;
}

/**
 * Group headers + counts for the active grouping. A failed facets call hides
 * the counts rather than taking the list down. `revision` re-reads them as
 * conversations arrive or leave; omit it to pin the facets to the query alone.
 */
export function useInboxFacets(query: InboxQuery, enabled: boolean, revision = 0): UseInboxFacets {
  return useFacetsBy(GROUP_STRATEGIES[query.grouping.by].facet, query, enabled, revision);
}

/**
 * The same, for a dimension named outright rather than derived from a grouping.
 *
 * The filter dialog's rows need options for dimensions nothing groups by —
 * nobody groups an inbox by skill — so the dimension is the caller's to give.
 */
export function useFacetsBy(
  dim: InboxFacetDim | null,
  query: InboxQuery,
  enabled: boolean,
  revision = 0,
): UseInboxFacets {
  const [state, setState] = useState<Omit<UseInboxFacets, 'loading'>>({ facets: [] });
  const [loading, setLoading] = useState(false);

  const key = JSON.stringify(compileInboxQuery(query));

  useEffect(() => {
    if (!enabled || !dim) {
      setState((prev) => (prev.facets.length === 0 ? prev : { facets: [] }));
      return;
    }
    let alive = true;
    setLoading(true);

    void facetInbox({ ...compileInboxQuery(query), by: dim })
      .then((res) => {
        if (alive) {
          setState({
            facets: res.facets,
            ...(res.unknown_count === undefined ? {} : { unknownCount: res.unknown_count }),
            ...(res.truncated === undefined ? {} : { truncated: res.truncated }),
          });
        }
      })
      .catch(() => {
        if (alive) setState({ facets: [] });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [key, dim, enabled, revision]);

  return { ...state, loading };
}

/**
 * The newest Info threads, read or not. Not `scope: unread`: that clause is
 * need-reply plus a watermark, and stream-chat-backend keeps no reply state for
 * marketing channels, so it never matches one and the Info dot never lit.
 * No status clause: an Info thread is never ended.
 */
const INFO_RECENT_QUERY: InboxQueryBody = {
  filters: [{ kind: 'kind', kinds: ['info'] }],
  limit: 50,
};

/** Unread chats, as the read model counts them. */
async function countUnreadChats(): Promise<number> {
  return (await queryInbox(UNREAD_CHATS_QUERY)).total;
}

/**
 * Unread Info threads: the read model says which threads are Info, Stream
 * says which of them hold unread messages. Stream's count includes system
 * notes, which is why the Chats rail does not use it (AI-2837); an Info thread
 * carries almost none, and a Jobie card the recruiter has not opened must light.
 */
async function countUnreadInfo(client: StreamChat): Promise<number> {
  const [{ channel_ids }, { channels }] = await Promise.all([
    queryInbox(INFO_RECENT_QUERY),
    client.getUnreadCount(),
  ]);
  const info = new Set(channel_ids);
  return channels.filter((c) => c.unread_count > 0 && info.has(cidParts(c.channel_id).channelId))
    .length;
}

// One engine for both rails, as `usePolledCount` is one for its callers; each
// rail brings its own count. `count` must be stable (module-level), or the
// effect rebuilds every render. Not gated on whether the dot is drawn, or the
// effect rebuilds and misses events (AI-2645).
// Null until the first answer: zero is a real result, not "not asked yet".
function useUnreadTotal(
  client: StreamChat | null,
  count: (client: StreamChat) => Promise<number>,
): number | null {
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    if (!client) return;
    let alive = true;
    // Compare `applied`, not `issued`: the catch supplies no replacement, so a
    // newer check that 429s would discard an older correct answer.
    let issued = 0;
    let applied = 0;

    const check = async () => {
      const seq = ++issued;
      try {
        const total = await count(client);
        if (!alive || seq < applied) return;
        applied = seq;
        setUnread(total);
      } catch {
        // Keep the last answer. A 500 or a rate-limited 429 is not news that the
        // employer has read everything, and clearing on it takes a dot off the
        // screen they were just looking at.
      }
    };

    void check();
    // Stream's counter is the cheapest "something moved" signal on the socket,
    // so re-check on it rather than polling — at the aggregates' cadence,
    // since reads are what drive this event (AI-2956).
    const gate = throttled(() => void check(), COUNT_REFRESH_MS);
    const sub = client.on((event) => {
      if (typeof event.total_unread_count !== 'number') return;
      gate.ask();
    });

    return () => {
      alive = false;
      gate.cancel();
      sub.unsubscribe();
    };
  }, [client, count]);

  return unread;
}

/** Whether any marketing ("Info") conversation is unread — the rail's dot. */
export function useInfoUnread(client: StreamChat | null): boolean {
  return (useUnreadTotal(client, countUnreadInfo) ?? 0) > 0;
}

/** Live total of `UNREAD_CHATS_QUERY` — the rail dot, widget badge and host navbar. */
export function useChatsUnreadCount(client: StreamChat | null): number | null {
  return useUnreadTotal(client, countUnreadChats);
}

/** What `useMessageHits` returns: the hits, and the term they actually answer,
 *  which lags the box by the panel's debounce plus a round trip. */
export interface MessageHitsState {
  hits: MessageHitDto[];
  term: string;
}

/**
 * The messages behind a search, from the BFF's memoised Stream walk — the same
 * walk the inbox query resolves through, so this burst shares one upstream
 * call. A hook rather than a call because the fetch has a lifetime: a reply
 * landing after a later keystroke is dropped rather than rendered, or the
 * marks would answer a term the box no longer says.
 *
 * A failed or unavailable search reports no hits rather than an error state:
 * the inbox list beside it already degrades to name-only and says so.
 */
export function useMessageHits(text: string): MessageHitsState {
  const [state, setState] = useState<MessageHitsState>({ hits: [], term: '' });
  const needle = text.trim();

  useEffect(() => {
    if (needle === '') {
      setState({ hits: [], term: '' });
      return;
    }
    let cancelled = false;
    void fetchMessageHits(needle)
      .then((res) => {
        if (cancelled) return;
        setState({ hits: res.hits, term: needle });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ hits: [], term: needle });
      });
    return () => {
      cancelled = true;
    };
  }, [needle]);

  return state;
}
